import { createSelector } from '@reduxjs/toolkit';
import { deploymentApi } from '../../services/deployment.api';

export const getMeasurementId = createSelector(
  [deploymentApi.endpoints.fetchEnvironment.select(undefined)],
  (endpoint) => endpoint.data?.googleAnalytics.measurementId || '',
);

export const selectDeploymentResult = createSelector(
  [deploymentApi.endpoints.fetchDeployment.select(undefined)],
  (endpoint) => endpoint.data || {},
);

export const getApiUrlPrefix = createSelector(
  [selectDeploymentResult],
  (data) => data.apiUrlPrefix || '',
);

export const getOldDashboardUrl = createSelector(
  [selectDeploymentResult],
  (data) => data.oldDashboardUrl || '',
);

export const getLegacyApiUrlPrefix = createSelector(
  [selectDeploymentResult],
  (data) => data.legacyApiUrlPrefix || '',
);

export const getGeoapifyApiKey = createSelector(
  [selectDeploymentResult],
  (data) => data.geoapifyApiKey || '',
);

export const getSparkUrl = createSelector([selectDeploymentResult], (data) => data.sparkUrl || '');

export const getSparkEnvironment = createSelector(
  [selectDeploymentResult],
  (data) => data.sparkEnvironment || '',
);

export const getIdleFeesEnabled = createSelector(
  [selectDeploymentResult],
  // Idle fee feature flag may be removed in future and hence we should show if the flag is missing
  // For more context https://chargelab.atlassian.net/browse/WEB-3144?focusedCommentId=46530
  (data) => (data.idleFeesEnabled !== undefined ? data.idleFeesEnabled : true),
);

/**
 * Based on Lenko and Simon's request, a temporary modification is being made.
 * The backend response now includes color(blue/green) information. This is causing errors
 *  in the printed QR code links. As a short-term solution, a fixed prod link
 * is being used, but this will result in errors for other environments.
 */
export const getWebAppUrlPrefix = createSelector(
  [selectDeploymentResult],
  (data) => 'https://charge.id', // data.webAppUrlPrefix || '', //
);

/**
 * Returns the webAppUrlPrefix from deployment data with any
 * deployment color (blue/green) stripped from the subdomain, so receipt URLs
 * resolve correctly on every environment.
 *
 * Examples:
 *   https://webapp-v1-blue.chargelab.io       → https://webapp.chargelab.io
 *   https://webapp-green.v1-shared.dev.chargelab.io → https://webapp.v1-shared.dev.chargelab.io
 *
 * Strategy: the first subdomain component is everything before the first ".".
 * Split it on "-" and keep only the first token (the base service name).
 */
export const getReceiptWebAppUrlPrefix = createSelector(
  [selectDeploymentResult],
  (data): string => {
    const raw = data.webAppUrlPrefix;
    if (!raw) return '';
    try {
      const parsed = new URL(raw);
      const parts = parsed.hostname.split('.');
      parts[0] = parts[0].split('-')[0];
      parsed.hostname = parts.join('.');
      return parsed.origin;
    } catch {
      return raw;
    }
  },
);
